import {useState, useRef} from 'react';
import {useNavigate, Link} from 'react-router';
import dayjs from 'dayjs';

import * as OralPhotoApi from '../api/OralPhotoApi';
import PageShell from '../components/PageShell';
import ModelViewer from '../components/ModelViewer';
import './OralPhotos.css';
import {problemMessage} from '../api/client';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';

const TOKENS = {
  primary: '#C43B71',
  blush: '#FFF0F5',
  surfaceLow: '#F9FAFB',
  ghostBorder: 'rgba(196, 59, 113, 0.16)',
  ink: '#1A1A2E',
  inkMuted: '#6B7280',
};

const FONT = {
  body: 'var(--font-body), Manrope, system-ui',
};

export default function OralPhotosAdd() {
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const fileInputRef = useRef(null);

  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [type, setType] = useState('Photo');
  const [fileLink, setFileLink] = useState(null);
  const [fileName, setFileName] = useState('');
  const [note, setNote] = useState('');

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const MAX_SIZE_MB = 20;
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_SIZE_MB) {
        setErrors((prev) => ({...prev, file: 'File size exceeds 20 MB. Please upload smaller file'}));
        return;
      }
      setErrors((prev) => ({...prev, file: null}));
      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (event) => {
        setFileLink(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!fileLink) newErrors.file = 'Choose a photo, X-ray, or 3D scan';
    if (!date) newErrors.date = 'Choose a date';
    if (!type) newErrors.type = 'Choose a type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    setSubmitting(true);

    const newOralPhoto = {
      shoot_date: date,
      photo_type: type,
      image_link: fileLink,
      file_name: fileName,
      note: note,
    };

    keys
      .run('oral-photo', newOralPhoto, (idempotencyKey) =>
        OralPhotoApi.createOralPhoto(newOralPhoto, {idempotencyKey}),
      )
      .then(() => {
        navigate('/oral-photo/');
      })
      .catch((err) => {
        setFormError(problemMessage(err, 'Failed to add photo. Please try again.'));
        setSubmitting(false);
      });
  };

  return (
    <PageShell title="Add to storage" showBackButton={true}>
      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: 20}}>
        <div className="oral-photos-form-card">
          <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
            <div className="oral-photos-field">
              <label htmlFor="dateInput" className="oral-photos-field-label">
                Date taken
              </label>
              <div className="oral-photos-field-hint">The day it was taken or scanned</div>
              <input
                id="dateInput"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`oral-photos-input ${errors.date ? 'invalid' : ''}`}
              />
              {errors.date && <div className="oral-photos-field-error">{errors.date}</div>}
            </div>

            <div className="oral-photos-field">
              <label htmlFor="typeSelect" className="oral-photos-field-label">
                Type
              </label>
              <select
                id="typeSelect"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={`oral-photos-input ${errors.type ? 'invalid' : ''}`}
              >
                <option value="Photo">Photo</option>
                <option value="X-ray">X-ray</option>
                <option value="3D">3D scan (.stl, .ply)</option>
              </select>
              {errors.type && <div className="oral-photos-field-error">{errors.type}</div>}
            </div>

            <div className="oral-photos-field">
              <label className="oral-photos-field-label">Choose a photo, X-ray, or 3D scan</label>
              <div className="oral-photos-field-hint">JPG, PNG, STL, or PLY, up to 20 MB</div>

              <input
                type="file"
                accept="image/*,.stl,model/stl,.ply,model/ply"
                ref={fileInputRef}
                style={{display: 'none'}}
                onChange={handleFileChange}
              />

              <div
                className={`oral-photos-drop-zone ${errors.file ? 'invalid' : ''}`}
                onClick={() => fileInputRef.current.click()}
              >
                {fileLink ? (
                  type === '3D' ? (
                    <div style={{width: '100%', height: '100%'}} onClick={(e) => e.stopPropagation()}>
                      <div className="oral-photos-drop-zone-text" style={{marginBottom: 12}}>
                        {fileName || '3D Model Selected'}
                      </div>
                      <ModelViewer
                        url={fileLink}
                        fileType={fileName.toLowerCase().endsWith('.ply') ? 'ply' : 'stl'}
                      />
                    </div>
                  ) : (
                    <img
                      src={fileLink}
                      alt="Preview"
                      style={{
                        maxHeight: 200,
                        borderRadius: 12,
                        display: 'block',
                        maxWidth: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  )
                ) : (
                  <>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{marginBottom: 12}}>
                      <path
                        d="M12 4v16m-8-8h16"
                        stroke={TOKENS.primary}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="oral-photos-drop-zone-text">Click to upload</div>
                  </>
                )}
              </div>
              {errors.file && <div className="oral-photos-field-error">{errors.file}</div>}
            </div>

            <div className="oral-photos-field">
              <label htmlFor="noteInput" className="oral-photos-field-label">
                Note
              </label>
              <textarea
                id="noteInput"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add any notes about this file (optional)"
                className={`oral-photos-textarea ${errors.note ? 'invalid' : ''}`}
              />
              {errors.note && <div className="oral-photos-field-error">{errors.note}</div>}
            </div>
          </div>
        </div>

        {formError && (
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              background: '#FEF2F2',
              border: '1px solid #DC2626',
              color: '#B91C1C',
              fontFamily: FONT.body,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {formError}
          </div>
        )}

        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          <p
            style={{
              fontFamily: FONT.body,
              fontSize: 13,
              lineHeight: 1.6,
              color: TOKENS.inkMuted,
              margin: 0,
            }}
          >
            Your files are encrypted and only you and the dentists you choose can see them.
          </p>
          <Link
            to="/privacy"
            style={{
              fontFamily: FONT.body,
              fontSize: 14,
              fontWeight: 700,
              color: TOKENS.primary,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Privacy Policy
          </Link>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="oral-photos-add-btn"
          style={{justifyContent: 'center', width: '100%', height: 56, fontSize: 16}}
        >
          {submitting ? 'Saving...' : 'Add to storage'}
        </button>
      </form>
    </PageShell>
  );
}
